import {
    faFilterCircleXmark,
    faPencil,
    faXmark,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowsFromLine,
    faArrowsToLine,
} from "@fortawesome/sharp-light-svg-icons";
import { Popover } from "@headlessui/react";
import * as d3 from "d3";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { DateTime } from "luxon";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useResizeObserver } from "../../../ui/Plot/hooks";
import { formatDate } from "../../../utils/dates";
import type {
    DataPointMapWithBin,
    EmissionRecordMapWithBin,
} from "../CustomTypes";
import { MapDataContext } from "../context";
import { useMapData, useMapFilters } from "../hooks/mapDataAndFilters";
import { DateRangeControl } from "./DataControlsV2/DateRangeControl";

interface DateRange {
    start?: string;
    end?: string;
}
interface DateRangeDisplayProps {
    clearSelection: () => void;
}

const DateRangeDisplay = (props: DateRangeDisplayProps) => {
    const { selectedContext } = useMapData("mainMap");
    const { filterState } = useMapFilters("mainMap");

    const dataDateRange = useMemo(() => {
        return {
            start: DateTime.fromISO(filterState.emissions.startDateFilter),
            end: DateTime.fromISO(filterState.emissions.endDateFilter),
        };
    }, [
        filterState.emissions.endDateFilter,
        filterState.emissions.startDateFilter,
    ]);
    const brushSelection = useMemo(() => {
        return {
            start: selectedContext.dateRangeToShowData?.start
                ? DateTime.fromISO(selectedContext.dateRangeToShowData.start)
                : undefined,
            end: selectedContext.dateRangeToShowData?.end
                ? DateTime.fromISO(selectedContext.dateRangeToShowData.end)
                : undefined,
        };
    }, [selectedContext.dateRangeToShowData]);

    return (
        <div className="w-fit flex gap-2 items-center text-sm">
            <Popover className="relative">
                {({ open }) => (
                    <>
                        <Popover.Button
                            className={`
                                flex items-center text-sm gap-2 rounded px-3
                                text-ae-slate-600 bg-ae-blue-50 bg-opacity-60 hover:bg-ae-blue-50
                                ${open ? "bg-ae-blue-100" : "bg-white"}
                            `}
                        >
                            From {formatDate(dataDateRange.start)} to{" "}
                            {formatDate(dataDateRange.end)}
                            <FontAwesomeIcon
                                icon={open ? faXmark : faPencil}
                                className="h-3 text-ae-blue-550"
                            />
                        </Popover.Button>
                        <Popover.Panel className="absolute z-10 -top-20 rounded bg-opacity-85">
                            <DateRangeControl />
                        </Popover.Panel>
                    </>
                )}
            </Popover>

            {(brushSelection.start || brushSelection.end) && (
                <button
                    type="button"
                    className="flex items-center text-sm gap-2 text-ae-blue-550 rounded px-3 bg-white bg-opacity-60 hover:bg-ae-blue-50"
                    onClick={() => {
                        props.clearSelection();
                    }}
                >
                    <span className="text-ae-slate-600">
                        (filtering
                        {brushSelection.start
                            ? ` from ${formatDate(brushSelection.start)}`
                            : ""}
                        {brushSelection.end
                            ? ` to ${formatDate(brushSelection.end)}`
                            : ""}
                        )
                    </span>
                    Clear selection
                    <FontAwesomeIcon
                        icon={faFilterCircleXmark}
                        className="h-3"
                    />
                </button>
            )}
        </div>
    );
};

const Legend = () => (
    <div className="flex items-center gap-4 mr-4">
        <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-ae-emission-self-reported"></div>
            <span className="text-xs text-gray-600">Company Monitoring</span>
        </div>
        <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-ae-emission-3rd-party"></div>
            <span className="text-xs text-gray-600">Third-Party</span>
        </div>
        <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[#BBDEFB]"></div>
            <span className="text-xs text-gray-600">Public</span>
        </div>
    </div>
);

interface BrushChartProps {
    thirdPartyEmissions: EmissionRecordMapWithBin[];
    selfReportedEmissions: EmissionRecordMapWithBin[];
    publicEmissions: DataPointMapWithBin[];
    loading: boolean;
    dateRange: DateRange | undefined;
    dataDateRange: {
        start: Date;
        end: Date;
    };
    setDateRange: (value?: DateRange) => void;
}

const showTimelineAtom = atomWithStorage("showTimeline", true);

const BrushChart = (props: BrushChartProps) => {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const brushRef = useRef(null);
    const brushGroupRef = useRef(null);
    const dimensions = useResizeObserver(containerRef);
    const [showTimeline, setShowTimeline] = useAtom(showTimelineAtom);

    // Force re-render
    const [value, updateState] = useState(new Date());
    const forceUpdate = useCallback(() => updateState(new Date()), []);

    // Clear selection and force plot to rerender
    const clearSelection = useCallback(() => {
        props.setDateRange(undefined);
        forceUpdate();
        // Disable the dependency checks here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selfReportedData = useMemo(() => {
        const data = (props.selfReportedEmissions || [])
            .filter((i) => i.detectedRate)
            .map((item) => ({
                x: item.detectionTimestamp,
                y: 1,
            }));
        return data;
    }, [props.selfReportedEmissions]);

    const thirdPartyData = useMemo(() => {
        const data = (props.thirdPartyEmissions || [])
            .filter((i) => i.detectedRate)
            .map((item) => ({
                x: item.detectionTimestamp,
                y: 1,
            }));
        return data;
    }, [props.thirdPartyEmissions]);

    const publicData = useMemo(() => {
        const data = (props.publicEmissions || [])
            .filter((i) => i.detectedRate)
            .map((item) => ({
                x: item.detectionTimestamp,
                y: 1,
            }));
        return data;
    }, [props.publicEmissions]);

    // Create the D3 bar chart with brush
    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!showTimeline) return;

        // Return if start and end dates are not known or invalid.
        // Note: an Invalid Date (e.g. new Date(undefined)) is a truthy object,
        // so we must explicitly check getTime() to avoid a NaN D3 domain.
        if (
            !props.dataDateRange.start ||
            !props.dataDateRange.end ||
            Number.isNaN(props.dataDateRange.start.getTime()) ||
            Number.isNaN(props.dataDateRange.end.getTime())
        ) {
            return;
        }

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Set up dimensions
        const margin = { top: 3, right: 20, bottom: 25, left: 20 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Define time range
        const startDate = props.dataDateRange.start;
        const endDate = props.dataDateRange.end;

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        const x = d3.scaleTime().domain([startDate, endDate]).range([0, width]);

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Calculate bar width - total width per time slot and individual bar width
        const totalBarWidth = (width / 64) * 0.4;
        const individualBarWidth = totalBarWidth / 3;

        // Add public emissions bars (left position)
        chart
            .selectAll(".public-bar")
            .data(publicData)
            .join("rect")
            .attr("class", "public-bar")
            .attr("x", (d) => x(d.x) - totalBarWidth / 2)
            .attr("y", 0)
            .attr("width", individualBarWidth)
            .attr("height", height)
            .attr("fill", "#BBDEFB");

        // Add third party emissions bars (middle position)
        chart
            .selectAll(".third-party-bar")
            .data(thirdPartyData)
            .join("rect")
            .attr("class", "third-party-bar")
            .attr("x", (d) => x(d.x) - individualBarWidth / 2)
            .attr("y", 0)
            .attr("width", individualBarWidth)
            .attr("height", height)
            .attr("fill", "#007C91");

        // Add self-reported emissions bars (right position)
        chart
            .selectAll(".self-reported-bar")
            .data(selfReportedData)
            .join("rect")
            .attr("class", "self-reported-bar")
            .attr("x", (d) => x(d.x) + individualBarWidth / 2)
            .attr("y", 0)
            .attr("width", individualBarWidth)
            .attr("height", height)
            .attr("fill", "#9ACD32");

        // Add the x-axis
        chart
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height})`)
            .call(
                d3
                    .axisBottom(x)
                    .ticks(14)
                    .tickSize(5)
                    .tickSizeOuter(0)
                    .tickPadding(2),
            );

        chart.selectAll(".x-axis line").attr("stroke", "#ccc");
        chart
            .selectAll(".x-axis text")
            .attr("fill", "#999")
            .attr("font-size", "13px");

        chart
            .append("line")
            .attr("class", "x-axis-line")
            .attr("x1", -40)
            .attr("x2", width + 40)
            .attr("y1", height)
            .attr("y2", height)
            .attr("stroke", "#ccc")
            .attr("stroke-width", 1);

        // Add shaded areas (before brush so they render behind)
        const leftShade = chart
            .append("rect")
            .attr("class", "shade-left")
            .attr("y", -4)
            .attr("height", height + 4)
            .attr("fill", "rgba(237, 237, 237, 0.4)");

        const rightShade = chart
            .append("rect")
            .attr("class", "shade-right")
            .attr("y", -4)
            .attr("height", height + 4)
            .attr("fill", "rgba(237, 237, 237, 0.4)");

        // Add the brush
        const brush = d3
            .brushX()
            .extent([
                [0, 0],
                [width, height - 4],
            ])
            .on("start brush end", brushed);

        const brushGroup = chart.append("g").attr("class", "brush").call(brush);
        brushGroupRef.current = brushGroup;
        brushRef.current = brush;

        // Style the brush
        brushGroup
            .select(".selection")
            .attr("fill-opacity", 0)
            .attr("stroke-width", 0);

        brushGroup.selectAll(".handle").attr("fill", "#DCDCDC");

        // Create custom handles
        const customHandles = brushGroup
            .selectAll(".custom-handle")
            .data([{ type: "w" }, { type: "e" }])
            .enter()
            .append("g")
            .attr("class", "custom-handle")
            .attr("cursor", "ew-resize")
            .attr(
                "transform",
                (d) =>
                    `translate(${d.type === "w" ? 0 : width}, ${height / 2})`,
            );

        // Draw circle
        customHandles
            .append("circle")
            .attr("r", 10)
            .attr("fill", "white")
            .attr("stroke", "#ccc")
            .attr("stroke-width", 2);

        // Draw double arrow
        customHandles
            .append("path")
            .attr("d", "M-6,0L-2,-4L-2,-2L2,-2L2,-4L6,0L2,4L2,2L-2,2L-2,4Z") // simple double arrow
            .attr("fill", "gray");

        if (props.dateRange?.start && props.dateRange?.end) {
            const start = new Date(props.dateRange.start);
            const end = new Date(props.dateRange.end);
            const [x0, x1] = [x(start), x(end)];
            brushGroup.call(brush.move, [x0, x1]);
        } else {
            const [x0, x1] = [x(startDate), x(endDate)];
            brushGroup.call(brush.move, [x0, x1]);
        }

        // Brushed event handler
        function brushed(event) {
            if (event.selection) {
                // Get the selected dates from the brush selection
                const timeScale = d3
                    .scaleTime()
                    .domain([startDate, endDate])
                    .range([0, width]);

                const [x0, x1] = event.selection.map(timeScale.invert);
                const newStart = x0.toISOString();
                const newEnd = x1.toISOString();
                // Get current values or use defaults
                const currentStart = props.dateRange?.start;
                const currentEnd = props.dateRange?.end;

                // Check if start is within valid range
                if (x0 > startDate && x0 < endDate) {
                    // Update just the start date
                    props.setDateRange({
                        start: newStart,
                        end: currentEnd || newEnd, // Keep existing end or use new end if none exists
                    });
                }

                // Check if end is within valid range
                if (x1 > startDate && x1 < endDate) {
                    // Update just the end date
                    props.setDateRange({
                        start: currentStart || newStart, // Keep existing start or use new start if none exists
                        end: newEnd,
                    });
                }

                // If both are outside valid range, clear the selection
                if (x0 <= startDate && x1 >= endDate) {
                    props.setDateRange(undefined);
                }

                // Update left shaded area
                leftShade.attr("x", -40).attr("width", x(x0) - 3 + 40);

                // Update right shaded area
                rightShade
                    .attr("x", x(x1) + 3)
                    .attr("width", width - x(x1) - 3 + 20);

                // Update custom handles
                customHandles.attr("transform", (d) => {
                    const xPos =
                        d.type === "w"
                            ? event.selection[0]
                            : event.selection[1];
                    return `translate(${xPos}, ${height / 2})`;
                });

                // Highlight selected bars
                chart
                    .selectAll(".public-bar")
                    .attr("fill", (d: any) => {
                        return d.x >= x0 && d.x <= x1 ? "#BBDEFB" : "#E3F2FD";
                    })
                    .attr("opacity", (d: any) => {
                        return d.x >= x0 && d.x <= x1 ? 1 : 0.6;
                    });

                chart
                    .selectAll(".third-party-bar")
                    .attr("fill", (d: any) => {
                        return d.x >= x0 && d.x <= x1 ? "#007C91" : "#B2EBF2";
                    })
                    .attr("opacity", (d: any) => {
                        return d.x >= x0 && d.x <= x1 ? 1 : 0.6;
                    });

                chart
                    .selectAll(".self-reported-bar")
                    .attr("fill", (d: any) => {
                        return d.x >= x0 && d.x <= x1 ? "#9ACD32" : "#F1F8E9";
                    })
                    .attr("opacity", (d: any) => {
                        return d.x >= x0 && d.x <= x1 ? 1 : 0.6;
                    });
            }
        }
    }, [
        dimensions,
        clearSelection,
        selfReportedData,
        thirdPartyData,
        publicData,
        props.dataDateRange,
        value,
        showTimeline,
    ]);

    return (
        <div
            className={`
                w-full flex flex-col relative
                pointer-events-auto
                ${showTimeline ? "h-24" : "h-6"}
            `}
        >
            <div className="flex w-full justify-between items-center">
                <DateRangeDisplay clearSelection={clearSelection} />
                <div className="flex items-center gap-2">
                    <Legend />
                    <button
                        type="button"
                        onClick={() => setShowTimeline((i) => !i)}
                        className="text-sm w-24 px-2 mr-1 flex bg-gray-100 justify-center items-center gap-2 rounded hover:bg-ae-blue-40 hover:text-ae-blue-550"
                    >
                        {showTimeline ? (
                            <>
                                <FontAwesomeIcon
                                    icon={faArrowsToLine}
                                    className="w-3 h-3"
                                />
                                Collapse
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon
                                    icon={faArrowsFromLine}
                                    className="w-3 h-3"
                                />
                                Expand
                            </>
                        )}
                    </button>
                </div>
            </div>
            <div
                ref={containerRef}
                className={`relative flex-grow w-full overflow-hidden ${!showTimeline ? "hidden" : ""}`}
            >
                <svg ref={svgRef} className="w-full h-full" />
            </div>
        </div>
    );
};

export const EmissionsBrushChart = () => {
    const emissionData = useContext(MapDataContext);
    const { setSelectedContext, selectedContext } = useMapData("mainMap");
    const { filterState } = useMapFilters("mainMap");
    const dataDateRange = useMemo(() => {
        return {
            start: new Date(filterState.emissions.startDateFilter),
            end: new Date(filterState.emissions.endDateFilter),
        };
    }, [
        filterState.emissions.endDateFilter,
        filterState.emissions.startDateFilter,
    ]);

    return (
        <div className="px-1 pt-1 w-full mx-auto flex gap-2 items-center justify-center text-sm bg-white pointer-events-auto">
            <BrushChart
                selfReportedEmissions={
                    emissionData.visibleOnMap.selfReportedEmissions
                }
                thirdPartyEmissions={
                    emissionData.visibleOnMap.thirdPartyEmissions
                }
                publicEmissions={emissionData.visibleOnMap.publicEmissions}
                dateRange={selectedContext.dateRangeToShowData}
                setDateRange={(value) => {
                    setSelectedContext((sc) => ({
                        ...sc,
                        dateRangeToShowData: value,
                    }));
                }}
                dataDateRange={dataDateRange}
                loading={emissionData.loading}
            />
        </div>
    );
};
